import { useEffect, useRef, useState } from "react";
import { useGameClubContext } from "../Hook/GameClubContext";
import {
  GameClubMemberHeader,
  GameClubMemberHeaderItem,
  GameClubMemberTable,
  GameClubMemberTableItem,
  GameClubMemberTableItemMember,
  GameClubMemberWrapper,
} from "../Style/GameClubMemberStyles";
import {
  GameClubMemberResponse,
  getClubMember,
} from "../../../../api/game-club";
import { isArray } from "lodash";
import { LOCAL_STORAGE_ACCESS_KEY } from "../../../../utils/network";
import { getClubScore } from "./utils/rank";
import GameClubCalendar from "./component/GameClubCalendar";

interface GameClubMemberProps {
  isCalendarOpen: boolean;
  setIsCalendarOpen: (isCalendarOpen: boolean) => void;
}

export const GameClubMember = ({
  isCalendarOpen,
  setIsCalendarOpen,
}: GameClubMemberProps) => {
  const { selectClubId, myClub } = useGameClubContext();
  const [list, setList] = useState<GameClubMemberResponse[]>([]);
  const [sortKey, setSortKey] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc");
  const listRef = useRef<HTMLDivElement | null>(null);

  const [startDate, setStartDate] = useState<string | null>(null);
  const [endDate, setEndDate] = useState<string | null>(null);

  const handleStartDate = (date: string) => {
    setStartDate(date);
  };

  const handleEndDate = (date: string) => {
    setEndDate(date);
  };

  const utcTimeToYYYYMMDD = (utcTime: string) => {
    const date = new Date(utcTime);
    return date.toISOString().split("T")[0];
  };

  useEffect(() => {
    if (startDate && endDate) {
      getList(startDate, endDate);
    } else {
      getList();
    }
  }, [startDate, endDate]);

  const getList = async (startDate?: string, endDate?: string) => {
    if (!myClub) return;
    const token = localStorage.getItem(LOCAL_STORAGE_ACCESS_KEY);
    const accessToken = token ? token.replace("Bearer ", "") : "-";
    try {
      const res = await getClubMember(
        myClub?.clubId,
        accessToken,
        startDate,
        endDate
      );
      const members = res.payload.members;

      let sortedData = [...members];
      if (sortKey) {
        sortedData.sort((a, b) => {
          let valueA: any = a[sortKey as keyof GameClubMemberResponse];
          let valueB: any = b[sortKey as keyof GameClubMemberResponse];

          if (sortKey === "lastAccessAt") {
            valueA = new Date(valueA || 0).getTime();
            valueB = new Date(valueB || 0).getTime();
          }

          if (valueA < valueB) return sortDirection === "asc" ? -1 : 1;
          if (valueA > valueB) return sortDirection === "asc" ? 1 : -1;
          return 0;
        });
      }
      setList(sortedData);
    } catch (e) {
      console.error(e);
      setList([]);
    }
  };

  const handleSort = (key: string) => {
    if (sortKey === key) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortKey(key);
      setSortDirection("asc");
    }
  };

  useEffect(() => {
    getList();
  }, [selectClubId, sortKey, sortDirection]);

  return (
    <GameClubMemberWrapper ref={listRef}>
      <GameClubMemberHeader>
        <GameClubMemberHeaderItem width={17}></GameClubMemberHeaderItem>
        <GameClubMemberHeaderItem
          width={11}
          onClick={() => handleSort("handCount")}
        >
          <div className="name">핸드</div>
          <img
            src="image-web/game/club/arrow_down.svg"
            alt="arrow"
            className={
              sortKey === "handCount" && sortDirection === "desc"
                ? "rotate"
                : ""
            }
          />
        </GameClubMemberHeaderItem>
        <GameClubMemberHeaderItem
          width={12}
          onClick={() => handleSort("betAmount")}
        >
          <div className="name">베팅</div>
          <img
            src="image-web/game/club/arrow_down.svg"
            alt="arrow"
            className={
              sortKey === "betAmount" && sortDirection === "desc"
                ? "rotate"
                : ""
            }
          />
        </GameClubMemberHeaderItem>
        <GameClubMemberHeaderItem
          width={12}
          onClick={() => handleSort("winCount")}
        >
          <div className="name">승리</div>
          <img
            src="image-web/game/club/arrow_down.svg"
            alt="arrow"
            className={
              sortKey === "winCount" && sortDirection === "desc" ? "rotate" : ""
            }
          />
        </GameClubMemberHeaderItem>
        <GameClubMemberHeaderItem
          width={12}
          onClick={() => handleSort("tournamentJoinCount")}
        >
          <div className="name">참여</div>
          <img
            src="image-web/game/club/arrow_down.svg"
            alt="arrow"
            className={
              sortKey === "tournamentJoinCount" && sortDirection === "desc"
                ? "rotate"
                : ""
            }
          />
        </GameClubMemberHeaderItem>
        <GameClubMemberHeaderItem
          width={20}
          onClick={() => handleSort("joinedAt")}
        >
          <div className="name">가입일</div>
          <img
            src="image-web/game/club/arrow_down.svg"
            alt="arrow"
            className={
              sortKey === "joinedAt" && sortDirection === "desc" ? "rotate" : ""
            }
          />
        </GameClubMemberHeaderItem>
      </GameClubMemberHeader>
      <GameClubMemberTable>
        {isArray(list) &&
          list.map((item, index) => (
            <GameClubMemberTableItem key={index}>
              <GameClubMemberTableItemMember>
                <div className="name">{item.nickname}</div>
                <div className={`role ${item.role}`}>
                  {item.role === "LEADER" ? "클럽장" : "클럽원"}
                </div>
              </GameClubMemberTableItemMember>
              <div className="count">{getClubScore(item.handCount)}</div>
              <div className="count">{getClubScore(item.betAmount)}</div>
              <div className="count">{getClubScore(item.winCount)}</div>
              <div className="count">
                {getClubScore(item.tournamentJoinCount)}회
              </div>
              <div className="date">{utcTimeToYYYYMMDD(item.joinedAt)}</div>
            </GameClubMemberTableItem>
          ))}
      </GameClubMemberTable>
      {isCalendarOpen && (
        <GameClubCalendar
          startDate={startDate}
          endDate={endDate}
          handleStartDate={handleStartDate}
          handleEndDate={handleEndDate}
          onClose={() => setIsCalendarOpen(false)}
        />
      )}
    </GameClubMemberWrapper>
  );
};
